/**
 * Aurora Scientific Lead Intelligence Platform — CANONICAL TYPE CONTRACTS
 * =======================================================================
 *
 * This file is the single source of truth for every entity in the Aurora
 * data model. The API server, the enrichment worker, and the React client
 * all import these interfaces. DO NOT redefine these shapes elsewhere.
 *
 * Two-layer governance model (see app/db/schema.md):
 *   - PUBLIC DATA LAKE (global, shared across clients, NO client_id):
 *       Scientist, ScientistIdentifier, Institution, InstitutionAlias,
 *       Department, Lab, LabMember, Publication, Authorship, Grant,
 *       GrantInvestigator, ConferenceAbstract, AbstractAuthor,
 *       ResearchTopic, ScientistTopic, SourceAttribution
 *   - PER-CLIENT ISOLATED LAYER (client_id NOT NULL on every row):
 *       Client, User, ApiKey, LeadBatch, Lead, EnrichmentProfile,
 *       Score, Recommendation, Feedback, AuditEntry
 *
 * Conventions:
 *   - All IDs are UUID strings.
 *   - All timestamps are ISO-8601 strings (DB stores timestamptz).
 *   - `Record<string, unknown>` is used for JSONB raw-payload columns that
 *     preserve original source data for later phases.
 *   - Domain string-union types (statuses, roles, sources) live near the
 *     entity they describe and are exported individually.
 */
/** The PL-2 Source Intelligence dataset new runs join (Joan, 2026-10-21). */
export const SOURCE_INTELLIGENCE_DATASET_DEFAULT = 'industry-2026-pl2';
/**
 * How a run's questions were written (Joan, 2026-10-21) — the ONE intended
 * difference between Aurora's two Source Intelligence reports:
 *   PL-1 — the original architecture: broader scientific, technology and
 *          supplier questions; now supplied by Aurora (pasted), never drafted
 *          by BLIP.
 *   PL-2 — experiment-driven bench-scientist questions drafted by BLIP under
 *          the PL-2 rules, then reviewed.
 * Separate from promptLibraryId, which is the approval MECHANISM: 'PL-1'
 * there means "archived, pre-approval run" and the worker refuses it, while a
 * new PL-1-architecture run still goes through draft → QC → approve → lock.
 */
export const PROMPT_ARCHITECTURES = ['PL-1', 'PL-2'];
/** The dataset each architecture's runs join by default; a dataset never mixes them. */
export const SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE = {
    // The October 2026 study (preset industry-2026-10) is the PL-1 report; a
    // corrected PL-1 market joins it under the study's own id.
    'PL-1': 'industry-2026-10',
    'PL-2': SOURCE_INTELLIGENCE_DATASET_DEFAULT,
};
/** Short form for lists and headers. */
export const PROMPT_ARCHITECTURE_SHORT = {
    'PL-1': 'General (PL-1)',
    'PL-2': 'Technical (PL-2)',
};
/** Joan / Ramos 2026-10-21: the two reports are sold as General and Technical. */
export const PROMPT_ARCHITECTURE_LABELS = {
    'PL-1': 'General — PL-1 (Aurora supplies the questions)',
    'PL-2': 'Technical — PL-2 (BLIP drafts, Aurora reviews)',
};
/**
 * How a run's questions were approved, in words. `prompt_library_id` names
 * the approval mechanism, not the kind of questions (that is the
 * architecture): 'PL-1' is the pre-review archive, anything else was
 * reviewed and locked before research. Printing the raw id read as "PL-2
 * revision 1" on a PL-1 run (Joan, 2026-10-22).
 */
export function promptApprovalLabel(libraryId, revision) {
    if (!libraryId || libraryId === 'PL-1')
        return 'Pre-review archive (run before questions were reviewed and locked)';
    return `Reviewed and locked before research, revision ${revision ?? 1}`;
}
/**
 * The 15 market labels of the AI Source Intelligence study, spelled exactly
 * as the October 2026 preset (industry/presets.ts) so a PL-2 market pools and
 * compares with its PL-1 run. A Source Intelligence run must use one of them.
 */
export const SOURCE_INTELLIGENCE_MARKETS = [
    'Western Blotting / Protein Detection',
    'Microscopy & Imaging',
    'Cell Culture',
    'Antibodies',
    'Flow Cytometry',
    'General Lab Equipment',
    'Lab Automation',
    'Custom DNA / Gene Editing',
    'NGS / Sequencing',
    'Molecular Biology Reagents',
    'Single Cell',
    'Cell & Gene Therapy Manufacturing / Bioprocessing',
    'Mass Spectrometry',
    'Spatial Biology',
    'Proteins / Immunoassays',
];
// ---------------------------------------------------------------------------
// PL-2 prompt QC (spec §C/§F, 2026-10-20)
// ---------------------------------------------------------------------------
/** The prompt library new runs are generated under. */
export const PROMPT_LIBRARY_CURRENT = 'PL-2';
export const PROMPT_QC_RULE_LABELS = {
    R1: 'Supplier neutrality',
    R2: 'Scientific scope',
    R3: 'Stage separation',
    R4: 'One primary need',
    R5: 'Non-leading',
    R6: 'Natural scientific language',
    R7: 'Coverage and originality',
    SCOPE: 'Scope inputs',
    COUNT: 'Questions per stage',
};
export const PROMPT_REJECT_REASON_LABELS = {
    off_scope: 'Out of scope',
    wrong_stage: 'Wrong buying stage',
    not_scientific: 'Not scientifically sound',
    leading_or_named: 'Leading / names a supplier',
    duplicate: 'Duplicate',
    poor_wording: 'Poor wording',
    other: 'Other',
};
/**
 * Canonical source-category vocabulary — the "what AI cites when it answers"
 * classification. SINGLE SOURCE OF TRUTH for both client and server: the server
 * classifier (sources.ts) re-exports these as named SOURCE_* constants and
 * normalizes onto them; the report legend renders them; the wire fields below
 * are typed to `SourceCategory` so a non-canonical literal is a COMPILE error
 * (this is what stops the mis-cased 'Supplier-owned content' class of drift).
 * Add or rename a category HERE and nowhere else.
 */
export const SOURCE_CATEGORIES = [
    'Supplier Content',
    'Peer-reviewed / Society Publisher',
    'Scientific Society / Consortium',
    'Editorial / Media Brand',
    'Business / Market Intelligence',
    'Directory / Marketplace',
    'Core Facility / Academia',
    'Standard/Reference Database',
    'Social Media Platform',
    'Government Agency',
    'Wikipedia',
    'Newswire Service',
    'Protocol',
    'Unclassified',
    // Internal sentinel: the answer cited no external source at all (distinct from
    // 'Unclassified', which is a cited source we couldn't confidently type).
    'No sources',
];
/**
 * Supplier Content Type — WHAT KIND of supplier-owned content a Supplier
 * Content citation is, as distinct from `SourceCategory` (WHO owns the domain).
 * Joan's signed-off taxonomy ("Supplier Content Type Taxonomy and Rules",
 * 22 Sept 2026 — reference/2026-09-22_11-04-49/), replacing the 2026-09-01
 * draft set. These are her "Approved Supplier Content Type Values" verbatim;
 * the definitions and eleven classification rules live in the classifier
 * prompt (services/benchmark/supplierContentTypes.ts).
 */
export const PAGE_TYPES = [
    'Product',
    'Application Hub',
    'Protocol & Methods',
    'Technical & Educational',
    'Blogs',
    'Citations',
    'Webinars / Podcasts',
    'Corporate',
    'Unclassified',
];
/**
 * Supplier types that are directories/marketplaces (where buyers FIND suppliers)
 * rather than makers/sellers. SINGLE SOURCE OF TRUTH for "directory-ness":
 * segmented out of the manufacturer head-to-head, and classified as the
 * 'Supplier Directory' source category. Derive from this — never hardcode the
 * two literals (client `isDirectoryType`, server classifier both use it).
 */
export const DIRECTORY_SUPPLIER_TYPES = [
    'supplier_directory',
    'services_marketplace',
];
/** Is this supplier type a directory/marketplace (vs a maker/seller)? */
export function isDirectorySupplierType(t) {
    return t === 'supplier_directory' || t === 'services_marketplace';
}
/**
 * Coerce any stored value onto a valid SupplierType. The DB column is free-text
 * (no CHECK constraint), so a raw-SQL fix or rebuild could write a value outside
 * the union; anything unrecognized falls back to 'unknown' rather than silently
 * mis-segmenting a row into the manufacturer head-to-head.
 */
export function normalizeSupplierType(raw) {
    switch (raw) {
        case 'manufacturer':
        case 'distributor':
        case 'supplier_directory':
        case 'services_marketplace':
        case 'unknown':
            return raw;
        default:
            return 'unknown';
    }
}
export const COMPETITOR_GROUPS = [
    'supplier',
    'directory_marketplace',
    'editorial_media',
];
/** Human-readable heading for each competitor group (report sub-sections). */
export const COMPETITOR_GROUP_LABELS = {
    supplier: 'Suppliers',
    directory_marketplace: 'Directories & Marketplaces',
    editorial_media: 'Editorial & Media Brands',
};
/** Display label for a competitor group. */
export function competitorGroupLabel(g) {
    return COMPETITOR_GROUP_LABELS[g];
}
/**
 * Coerce any stored/free-text value onto a valid CompetitorGroup. Mirrors
 * `normalizeSupplierType`; anything unrecognized falls back to 'supplier'.
 */
export function normalizeCompetitorGroup(raw) {
    switch (raw) {
        case 'supplier':
        case 'directory_marketplace':
        case 'editorial_media':
            return raw;
        default:
            return 'supplier';
    }
}
/**
 * The group to assign a named competitor when the client did not pick one.
 * Derived from the dictionary type: directories/marketplaces group as such;
 * everything else (manufacturer / distributor / unknown / not-in-dictionary)
 * groups as 'supplier'. 'editorial_media' is only ever set by explicit client
 * choice, because media brands are not in the supplier dictionary.
 */
export function defaultCompetitorGroup(t) {
    return isDirectorySupplierType(t) ? 'directory_marketplace' : 'supplier';
}
// Pharma R&D added by the PL-2 spec §E (2026-10-20).
export const PROMPT_PERSONAS = [
    'academic_research_scientist',
    'pharma_rd_scientist',
    'biotech_scientist',
    'core_facility_scientist',
];
/** Human-readable label for each persona (create-run UI, report copy). */
export const PROMPT_PERSONA_LABELS = {
    academic_research_scientist: 'Academic Research Scientist',
    pharma_rd_scientist: 'Pharma R&D Scientist',
    core_facility_scientist: 'Core Facility Scientist',
    biotech_scientist: 'Biotech Scientist',
};
/** Display label for a prompt persona. */
export function promptPersonaLabel(p) {
    return PROMPT_PERSONA_LABELS[p];
}
/**
 * Coerce a stored/submitted value onto a valid PromptPersona, or null if it
 * isn't one of the four. Callers should drop (not coerce) an unrecognized
 * value — unlike CompetitorGroup, there is no sensible default persona.
 */
export function normalizePromptPersona(raw) {
    switch (raw) {
        case 'academic_research_scientist':
        case 'pharma_rd_scientist':
        case 'core_facility_scientist':
        case 'biotech_scientist':
            return raw;
        default:
            return null;
    }
}
/** Joan's client-facing labels for the four Comprehensibility sub-dimensions. */
export const COMPREHENSIBILITY_DIMENSION_LABELS = [
    { key: 'entityClarity', label: 'Entity Clarity' },
    { key: 'useCaseClarity', label: 'Use-Case Clarity' },
    { key: 'answerability', label: 'Answer Orientation' },
    { key: 'freshnessAuthorship', label: 'Authority & Evidence' },
];
/**
 * The report's section vocabulary, in Joan's required order (2026-08-22 §14).
 *
 * ONE list, read by BOTH the on-screen report and the Markdown export. Page and
 * document have drifted apart three times in this codebase when the same copy
 * was maintained in two renderers; a shared constant makes that class of drift
 * structurally impossible.
 */
export const CONTENT_REPORT_SECTIONS = [
    { key: 'executiveSummary', title: 'Executive Summary' },
    { key: 'scorecard', title: 'AI Content Discoverability™ Scorecard' },
    { key: 'understandsToday', title: 'What AI Understands Today' },
    { key: 'aiMissing', title: 'What AI Is Missing' },
    { key: 'benchmarkLinkage', title: 'Benchmark Linkage' },
    { key: 'technicalFindings', title: 'Technical Discoverability Findings' },
    { key: 'contentFindings', title: 'Content Discoverability Findings' },
    { key: 'pageFindings', title: 'Priority Page Findings' },
    { key: 'contentGaps', title: 'Content Gap Summary' },
    { key: 'priorityActions', title: 'Priority Action Plan: Start Here' },
    { key: 'additionalOpportunities', title: 'Additional Opportunities' },
    { key: 'longerTerm', title: 'Longer-Term Content Strategy' },
    { key: 'methodology', title: 'Methodology & Scoring' },
    { key: 'auroraInsight', title: 'Aurora Insight' },
];
/**
 * The sections to render for a given report, numbered CONSECUTIVELY over what is
 * actually present (a linked benchmark run is optional, and an old stored report
 * has none of the 2026-08 sections). Both renderers call this, so the numbering
 * and the titles cannot disagree between screen and document.
 */
export function contentReportSections(report) {
    const present = (key) => {
        switch (key) {
            case 'understandsToday':
                return (report.aiUnderstanding?.understandsToday.length ?? 0) > 0;
            case 'aiMissing':
                return (report.aiUnderstanding?.cannotDetermine.length ?? 0) > 0;
            case 'benchmarkLinkage':
                return Boolean(report.benchmarkLinkageDetail ?? report.benchmarkLinkage);
            case 'contentGaps':
                return (report.contentGaps?.length ?? 0) > 0;
            case 'priorityActions':
                return ((report.priorityActions?.length ?? 0) > 0 ||
                    (report.remediationRoadmap?.phase1?.length ?? 0) > 0);
            case 'additionalOpportunities':
                return ((report.additionalOpportunities?.length ?? 0) > 0 ||
                    (report.remediationRoadmap?.phase2?.length ?? 0) > 0 ||
                    (report.remediationRoadmap?.phase3?.length ?? 0) > 0);
            case 'longerTerm':
                return (Boolean(report.longerTermStrategy?.narrative) ||
                    (report.longerTermStrategy?.researcherQuestions.length ?? 0) > 0);
            default:
                // Executive summary, scorecard, findings, page findings, methodology
                // and the Aurora Insight are structural — every report has them.
                return true;
        }
    };
    let n = 0;
    return CONTENT_REPORT_SECTIONS.filter((s) => present(s.key)).map((s) => ({
        key: s.key,
        title: s.title,
        n: ++n,
    }));
}
/**
 * The Executive Summary's four questions, in Joan's order (§1), resolved against
 * whatever the stored report actually has.
 *
 * A report written before the rework has no `what*` fields, but it does carry the
 * same substance under other names — so each question falls back through the
 * legacy fields rather than rendering an empty heading. Shared by both renderers.
 */
export function contentExecutiveQuestions(report) {
    const e = report.executiveSummary;
    const d = report.diagnosis;
    const pick = (...candidates) => candidates.find((c) => c && c.trim().length > 0)?.trim() ?? '';
    return [
        { label: 'What is working', value: pick(e.whatIsWorking, d?.helping) },
        {
            label: 'What is limiting AI discoverability',
            value: pick(e.whatIsLimiting, d?.limiting, e.biggestBlocker),
        },
        {
            label: 'Why this matters',
            value: pick(e.whyItMatters, e.strategicOpportunity),
        },
        {
            label: 'What to do first',
            value: pick(e.whatToDoFirst, e.quickestWin, d?.mustChange, report.strategicPriority),
        },
    ].filter((q) => q.value.length > 0);
}
/**
 * The at-most-five priority actions (§10). Falls back to the legacy roadmap's
 * Phase 1, which is what "start here" meant before the rework — so a stored
 * report still produces a populated action plan rather than an empty section.
 */
export function contentPriorityActions(report) {
    if (report.priorityActions && report.priorityActions.length > 0) {
        return report.priorityActions.slice(0, 5);
    }
    return (report.remediationRoadmap?.phase1 ?? []).slice(0, 5).map((it) => ({
        title: it.recommendation,
        action: it.recommendation,
        whyItMatters: it.whyItMatters,
        dimension: it.dimension,
        effort: it.effort,
        impact: it.impact,
    }));
}
/**
 * The subordinate "Additional Opportunities" list (§11). Falls back to the
 * legacy roadmap's later phases, flattened to plain lines — deliberately a list
 * and not a table, so it cannot compete visually with the five actions above it.
 */
export function contentAdditionalOpportunities(report) {
    if (report.additionalOpportunities && report.additionalOpportunities.length) {
        return report.additionalOpportunities;
    }
    const r = report.remediationRoadmap;
    return [...(r?.phase2 ?? []), ...(r?.phase3 ?? [])].map((it) => it.recommendation);
}
/**
 * Finding → Potential AI Impact rows, split the way the report is now organised
 * (§5 with §14): accessibility and structure findings sit under Technical
 * Discoverability, comprehensibility findings under Content Discoverability.
 * Anything unclassified rides with the content group rather than disappearing.
 */
export function contentFrictionByGroup(report) {
    const rows = report.contentFriction ?? [];
    return {
        technical: rows.filter((r) => r.dimension === 'accessibility' || r.dimension === 'structure'),
        content: rows.filter((r) => r.dimension !== 'accessibility' && r.dimension !== 'structure'),
    };
}
/** The four Comprehensibility sub-dimensions as rows: label, rating, finding. */
export function comprehensibilityRows(findings) {
    return COMPREHENSIBILITY_DIMENSION_LABELS.map(({ key, label }) => ({
        label,
        rating: findings.ratings?.[key] ?? null,
        finding: (findings[key] ?? '').trim(),
    })).filter((row) => row.finding.length > 0 || row.rating !== null);
}
/**
 * The scorecard's two groups (Joan §2): technical discoverability is what can be
 * CHECKED, content discoverability is what must be JUDGED. Keeping the basis
 * visible per group is the audit's credibility hook, so this copy is shared by
 * both renderers rather than restated in each.
 */
export const CONTENT_SCORE_GROUPS = {
    technical: {
        title: 'Technical Discoverability',
        basis: 'Can AI systems reach, retrieve and parse the site? Both scores below are ' +
            'deterministic — they are computed from checkable facts about the site ' +
            '(crawler directives, how the page delivers its content, structured data, ' +
            'headings, metadata), not from an opinion about it.',
    },
    content: {
        title: 'Content Discoverability',
        basis: 'Once a page is parsed, does its content give AI enough context to ' +
            'understand the company, its expertise, its offerings and when they are ' +
            'relevant? This score is directional — a grounded AI judgment of the page ' +
            'text, not a measurement.',
    },
    overall: {
        title: 'Overall AI Content Discoverability',
        basis: 'A combined view of the site’s ability to be accessed, interpreted and ' +
            'used as a source of information. Accessibility gates the result: content ' +
            'quality cannot compensate for content AI cannot reach.',
    },
};
/**
 * The Methodology & Scoring section (Joan §14 item 13). Static, shared copy — it
 * describes how the audit works and therefore must never be model-generated.
 */
export const CONTENT_METHODOLOGY_COPY = [
    {
        heading: 'What this audit measures',
        body: 'Whether AI answer engines can access, parse and understand the ' +
            'company’s public web content. It is not a search-ranking review and ' +
            'not a design review.',
    },
    {
        heading: 'How the scores are produced',
        body: 'Accessibility and Structure are deterministic: each underlying signal is ' +
            'a checkable fact (a crawler is allowed or blocked; structured data is ' +
            'present or absent; the page has one H1 or it does not), and the score is ' +
            'the weighted share of those checks that passed. Comprehensibility is ' +
            'directional: an AI judgment of how clearly the page text explains the ' +
            'company, grounded in the text captured during the crawl.',
    },
    {
        heading: 'How Overall is calculated',
        body: 'Overall blends Structure and Comprehensibility and then gates the result ' +
            'on Accessibility, because a site AI cannot reach is invisible however ' +
            'well its content reads.',
    },
    {
        heading: 'What was examined',
        body: 'The site’s robots.txt, llms.txt and sitemap, plus a bounded set of ' +
            'public pages, fetched as a non-JavaScript AI crawler sees them. Only the ' +
            'company’s own public pages are fetched, and only with GET requests.',
    },
    {
        heading: 'Limitations',
        body: 'The report is a dated snapshot of public HTML under recorded conditions ' +
            '(user agent, page cap, model, framework version). It does not measure ' +
            'JavaScript-rendered content, and it cannot establish what any AI system ' +
            'will do with the content in future. Re-auditing after changes is a new ' +
            'dated run, which is what makes before-and-after comparison possible.',
    },
];
//# sourceMappingURL=types.js.map